import React, { useState } from 'react';
import { FONT_BODY } from './theme';
import { LOGO_SRC } from './theme';
import { CloseIcon } from './icons';

/* Reusable primitives. All values mirror the one-pager source. */

/* ---------- Logo ---------- */

export const Logo: React.FC<{ width: number; height: number }> = ({ width, height }) => (
  <img
    src={LOGO_SRC}
    alt="Soomuch"
    width={width}
    height={height}
    style={{ display: 'block', height, width: 'auto', mixBlendMode: 'multiply' }}
  />
);

/* ---------- Buttons ---------- */

type CtaVariant = 'header' | 'hero' | 'dark' | 'block';

const CTA_BASE: Record<CtaVariant, React.CSSProperties> = {
  header: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 0,
    cursor: 'pointer',
    background: '#2563EB',
    color: '#fff',
    borderRadius: 999,
    padding: '12px 20px',
    font: `600 14px ${FONT_BODY}`,
    boxShadow: '0 8px 20px rgba(37,99,235,.24)',
  },
  hero: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    border: 0,
    cursor: 'pointer',
    background: '#2563EB',
    color: '#fff',
    borderRadius: 999,
    padding: '16px 26px',
    font: `700 15.5px ${FONT_BODY}`,
    boxShadow: '0 12px 28px rgba(37,99,235,.3)',
  },
  dark: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    border: 0,
    cursor: 'pointer',
    background: '#0F2D6B',
    color: '#fff',
    borderRadius: 999,
    padding: '15px 26px',
    font: `700 15px ${FONT_BODY}`,
    boxShadow: '0 12px 26px rgba(15,45,107,.22)',
  },
  block: {
    border: 0,
    cursor: 'pointer',
    background: '#2563EB',
    color: '#fff',
  },
};

const CTA_HOVER_CLASS: Record<CtaVariant, string> = {
  header: 'sm1p-cta-header',
  hero: 'sm1p-cta-hero',
  dark: 'sm1p-btn-dark',
  block: 'sm1p-btn-block',
};

export const CtaButton: React.FC<{
  variant: CtaVariant;
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
  type?: 'button' | 'submit';
}> = ({ variant, onClick, style, children, type }) => (
  <button
    type={type || 'button'}
    onClick={onClick}
    className={CTA_HOVER_CLASS[variant]}
    style={{ ...CTA_BASE[variant], ...style }}
  >
    {children}
  </button>
);

export const IconCloseButton: React.FC<{ onClick: () => void; size?: number; label?: string }> = ({
  onClick,
  size = 32,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="sm1p-icon-btn"
    style={{
      marginLeft: 'auto',
      border: 0,
      background: '#F5F9FE',
      cursor: 'pointer',
      width: size,
      height: size,
      borderRadius: 9,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 'none',
    }}
  >
    <CloseIcon />
  </button>
);

/* ---------- Section kicker ---------- */

export const Kicker: React.FC<{ children: React.ReactNode; align?: 'left' | 'center' }> = ({
  children,
  align = 'left',
}) => (
  <p
    style={{
      font: `700 11px ${FONT_BODY}`,
      letterSpacing: '.26em',
      textTransform: 'uppercase',
      color: '#2563EB',
      margin: 0,
      ...(align === 'center' ? { textAlign: 'center' as const } : {}),
    }}
  >
    {children}
  </p>
);

/* ---------- Focusable form controls ---------- */

const FOCUS_STYLE: React.CSSProperties = {
  borderColor: '#2563EB',
  boxShadow: '0 0 0 4px rgba(37,99,235,.14)',
};

export function useFocus() {
  const [focused, setFocused] = useState(false);
  return {
    focused,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  };
}

export const FieldLabel: React.FC<{ children: React.ReactNode; marginBottom?: number }> = ({
  children,
  marginBottom = 7,
}) => (
  <label style={{ display: 'block', font: `600 12.5px ${FONT_BODY}`, color: '#0F172A', marginBottom }}>
    {children}
  </label>
);

export const FieldError: React.FC<{ children: React.ReactNode; fontSize?: number; margin?: string }> = ({
  children,
  fontSize = 12,
  margin = '5px 0 0',
}) => (
  <p style={{ font: `500 ${fontSize}px ${FONT_BODY}`, color: '#DC2626', margin }}>{children}</p>
);

export interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  borderColor?: string;
  fontSize?: 15 | 16;
  padding?: string;
  fontWeight?: number;
  background?: string;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { borderColor = '#CFDFF6', fontSize = 16, padding = '12px 14px', fontWeight = 500, background = '#FBFDFF', style, onFocus, onBlur, ...rest },
  ref,
) {
  const focus = useFocus();
  return (
    <input
      ref={ref}
      {...rest}
      onFocus={(e) => {
        focus.onFocus();
        if (onFocus) onFocus(e);
      }}
      onBlur={(e) => {
        focus.onBlur();
        if (onBlur) onBlur(e);
      }}
      style={{
        width: '100%',
        background,
        border: `1px solid ${focus.focused ? '#2563EB' : borderColor}`,
        borderRadius: 11,
        padding,
        font: `${fontWeight} ${fontSize}px ${FONT_BODY}`,
        color: '#0F172A',
        outline: 'none',
        ...(focus.focused ? { boxShadow: FOCUS_STYLE.boxShadow } : {}),
        ...style,
      }}
    />
  );
});

export const ChevronForSelect: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#64748B"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ position: 'absolute', right: 14, top: '50%', marginTop: -6, pointerEvents: 'none' }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  borderColor?: string;
  placeholderLabel: string;
  options: string[];
}

export const SelectField: React.FC<SelectFieldProps> = ({
  borderColor = '#CFDFF6',
  placeholderLabel,
  options,
  value,
  style,
  onFocus,
  onBlur,
  ...rest
}) => {
  const focus = useFocus();
  const empty = value === '' || value === undefined;
  return (
    <div style={{ position: 'relative' }}>
      <select
        {...rest}
        value={value}
        onFocus={(e) => {
          focus.onFocus();
          if (onFocus) onFocus(e);
        }}
        onBlur={(e) => {
          focus.onBlur();
          if (onBlur) onBlur(e);
        }}
        style={{
          width: '100%',
          background: '#FBFDFF',
          border: `1px solid ${focus.focused ? '#2563EB' : borderColor}`,
          borderRadius: 11,
          padding: '12px 14px',
          font: `500 16px ${FONT_BODY}`,
          color: '#0F172A',
          outline: 'none',
          appearance: 'none',
          WebkitAppearance: 'none',
          paddingRight: 38,
          cursor: 'pointer',
          ...(empty ? { color: '#94A3B8' } : { color: '#0F172A' }),
          ...(focus.focused ? { boxShadow: FOCUS_STYLE.boxShadow } : {}),
          ...style,
        }}
      >
        <option value="" disabled>
          {placeholderLabel}
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <ChevronForSelect />
    </div>
  );
};

/* ---------- Modal shell ---------- */

export const ModalShell: React.FC<{
  zIndex: number;
  overlayPadding?: string;
  panelMaxWidth: number;
  panelStyle?: React.CSSProperties;
  onClose: () => void;
  children: React.ReactNode;
  dialogLabel?: string;
}> = ({ zIndex, overlayPadding = 'clamp(14px,3vw,36px)', panelMaxWidth, panelStyle, onClose, children, dialogLabel }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      zIndex,
      background: 'rgba(15,23,42,.55)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: overlayPadding,
      animation: 'sm1p-fadein .18s ease both',
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      role={dialogLabel ? 'dialog' : undefined}
      aria-modal={dialogLabel ? true : undefined}
      aria-label={dialogLabel}
      style={{
        width: '100%',
        maxWidth: panelMaxWidth,
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 40px 90px -20px rgba(15,23,42,.5)',
        overflow: 'hidden',
        animation: 'sm1p-slidein .26s ease both',
        ...panelStyle,
      }}
    >
      {children}
    </div>
  </div>
);

export const ModalHeader: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid #F1F6FD' }}>
    <Logo width={98} height={18} />
    <span style={{ font: `700 13.5px ${FONT_BODY}`, color: '#0F172A' }}>{title}</span>
    <IconCloseButton onClick={onClose} />
  </div>
);
